import { CloseOutlined } from '@ant-design/icons';
import { Button, Drawer, Tabs } from 'antd';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import ErrorResult from '../components/ErrorResult';
import PageTitle from '../components/PageTitle';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams, { Optional } from '../hooks/useTypedParams';
import { CandidateId, JobPostingId, RecruitmentProcessCollection } from '../types';
import styles from './CandidateInterviewNavigator.module.css';

const CandidateInterviewNavigator = () => {
  const location = useLocation();
  const navigate = useTypedNavigate();
  const activeTabKey = location.pathname.replace(/\/$/, '').split('/').pop() || 'details';
  const [searchParams] = useSearchParams();
  const { tenant, id, applicant_id, interview_id } = useTypedParams({
    tenant: String,
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
    interview_id: String,
  });
  const queryClient = useQueryClient();

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: applicant_id,
          job_posting: id,
        },
      },
    ],
    {
      enabled: Boolean(id),
      select(data) {
        return data.results[0];
      },
    }
  );

  const recruitmentProcessVisualizationMutation = useAPIMutation(
    async (
      { recruitmentProcessId }: { recruitmentProcessId: RecruitmentProcessCollection['id'] },
      api
    ) => {
      await api.post('/recruitment-process-user-visualizations/', {
        recruitment_process: recruitmentProcessId,
      });
    },
    {
      onSuccess: async (_, __) => {
        await queryClient.invalidateQueries([
          '/recruitment-processes/',
          {
            params: {
              candidate: applicant_id,
              job_posting: id,
            },
          },
        ]);
        await queryClient.invalidateQueries(['useSearchCandidates']);
      },
    }
  );

  // For recruitment process visualization
  useEffect(() => {
    if (
      !recruitmentProcessVisualizationMutation.isLoading &&
      !recruitmentProcess.isFetching &&
      recruitmentProcess.data?.id &&
      !recruitmentProcess.data?.recruiter_visualization_id &&
      id
    ) {
      recruitmentProcessVisualizationMutation.mutate({
        recruitmentProcessId: recruitmentProcess.data.id,
      });
    }
  }, [
    recruitmentProcess.data?.id,
    recruitmentProcess.data?.recruiter_visualization_id,
    recruitmentProcess.isFetching,
    id,
    recruitmentProcessVisualizationMutation,
  ]);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const onClose = () =>
    navigate({
      pathname: '/:tenant/opportunities/:id/interview/:interview_id/',
      params: { tenant, id, interview_id },
      search: searchParams,
    });

  return (
    <Drawer
      open
      size="large"
      maskClosable
      onClose={onClose}
      className={styles.drawer}
      closable={false}
      width={870}
      title={
        <PageTitle
          title={candidate.data?.name || '...'}
          level={2}
          extra={[
            <Button key="close" type="text" onClick={onClose} shape="circle">
              <CloseOutlined />
            </Button>,
          ]}
          footer={
            <Tabs
              activeKey={activeTabKey}
              onChange={(key) => {
                switch (key) {
                  case 'details':
                    navigate(
                      {
                        pathname:
                          '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/details/',
                        params: { tenant, id, applicant_id, interview_id },
                        search: searchParams,
                      },
                      { replace: true }
                    );
                    break;
                  case 'attachments':
                    navigate(
                      {
                        pathname:
                          '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/attachments/',
                        params: { tenant, id, applicant_id, interview_id },
                        search: searchParams,
                      },
                      { replace: true }
                    );
                    break;
                  case 'questionnaires':
                    navigate(
                      {
                        pathname:
                          '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/questionnaires/',
                        params: { tenant, id, applicant_id, interview_id },
                        search: searchParams,
                      },
                      { replace: true }
                    );
                    break;
                  case 'history':
                    navigate(
                      {
                        pathname:
                          '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/history/',
                        params: { tenant, id, applicant_id, interview_id },
                        search: searchParams,
                      },
                      { replace: true }
                    );
                    break;
                  case 'tests':
                    navigate(
                      {
                        pathname:
                          '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/tests/',
                        params: { tenant, id, applicant_id, interview_id },
                        search: searchParams,
                      },
                      { replace: true }
                    );
                    break;
                  case 'custom-tests':
                    navigate(
                      {
                        pathname:
                          '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/custom-tests/',
                        params: { tenant, id, applicant_id, interview_id },
                        search: searchParams,
                      },
                      { replace: true }
                    );
                    break;
                  case 'interviews':
                    navigate(
                      {
                        pathname:
                          '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/interviews/',
                        params: { tenant, id, applicant_id, interview_id },
                        search: searchParams,
                      },
                      { replace: true }
                    );
                    break;
                }
              }}
              items={[
                { key: 'details', label: 'Dados' },
                { key: 'attachments', label: 'Anexos' },
                { key: 'questionnaires', label: 'Perguntas de pré-requisito' },
                { key: 'history', label: 'Histórico' },
                { key: 'tests', label: 'Testes' },
                ...(config.data?.enableAlchemerIntegration
                  ? [{ key: 'custom-tests', label: 'Testes Customizados' }]
                  : []),
                ...(config.data?.t002_enableCandidateInterview
                  ? [{ key: 'interviews', label: 'Entrevistas' }]
                  : []),
              ]}
            />
          }
        />
      }
    >
      <ErrorResult error={candidate.error}>
        <Outlet />
      </ErrorResult>
    </Drawer>
  );
};

export default CandidateInterviewNavigator;
